import { type ActiveState, createEntityStore, type EntityState } from '@datorama/akita';
import { type KpiDefinitions, type KpiDetailsResponse, type LegacyKpiDetailsResponse, type Period } from 'types/models';

/**
 * Type for KPI details data stored in state.
 * Can be either a normalized response (without kpiDefinitions), a legacy response, or null.
 */
export type KpiDetailsData = Omit<KpiDetailsResponse, 'kpiDefinitions'> | LegacyKpiDetailsResponse | null;

// Import EntityStatusRecord interface or define it here
export interface EntityStatusRecord {
   id: number;
   periodId: number;
   organisationId: number;
   kpiAreaId: number | null;
   kpiId: number | null;
   kpiSubId: number | null;
   isEnabled: boolean;
   createdBy?: string;
   updatedBy?: string;
   createdAt?: string;
   updatedAt?: string;
}

export interface ValidationStatusRecord {
   id?: number;
   organisationId?: number;
   kpiSubId: number;
   validationRuleNoValue: number;
   validationRuleSoft: number;
   validationRuleStrict: number;
   validationRulesInRange: number;
   validFrom?: string;
   validTo?: string;
   createdAt?: string;
   updatedAt?: string;
   organisationCount?: number; // Only present when aggregate=true
}

export interface ValidationStatusHierarchyItem {
   id: number;
   worstStatus: 'error' | 'warning' | 'success' | 'default';
   validationRuleStrict: number;
   validationRuleSoft: number;
   validationRuleNoValue: number;
   validationRulesInRange: number;
   kpiAreaId?: number; // Only for KPIs
   kpiId?: number; // Only for KPI Subs
}

export interface ValidationStatusOverall {
   worstStatus: 'error' | 'warning' | 'success' | 'default';
   validationRuleStrict: number;
   validationRuleSoft: number;
   validationRuleNoValue: number;
   validationRulesInRange: number;
}

export interface ValidationStatusHierarchyResponse {
   organisationId: number;
   overall: ValidationStatusOverall;
   kpiAreas: Record<string, ValidationStatusHierarchyItem>;
   kpis: Record<string, ValidationStatusHierarchyItem>;
   kpiSubs: Record<string, ValidationStatusHierarchyItem>;
}

export interface CompletionStats {
   closedOrganizations: {
      percentage: number;
      total: number;
      closed: number;
   };
   resolvedValidations: {
      percentage: number;
      total: number;
      resolved: number;
   };
   closedKpiAreas: {
      percentage: number;
      total: number;
      closed: number;
   };
}

export interface ClosingHubJobStatus {
   id: number;
   type: string;
   status: 'PENDING' | 'SUCCESS' | 'ERROR' | 'CANCELLED';
   error?: string;
   result?: Record<string, unknown>;
   startedAt?: string;
   completedAt?: string;
}

export interface EntityPendingJob {
   id: number;
   action: 'enable' | 'disable' | 'close' | 'bulk';
   status: 'PENDING' | 'SUCCESS' | 'ERROR' | 'CANCELLED';
   cascade?: boolean;
   organisationId: number;
   kpiAreaId?: number;
   kpiId?: number;
   kpiSubId?: number;
   startedAt?: string;
}

export interface EntityStatusResponse {
   statusRecords: EntityStatusRecord[];
   pendingJob: EntityPendingJob | null;
}

export type BulkClosingEntityType = 'organisation' | 'kpiArea' | 'kpi' | 'kpiSub';

export interface BulkClosingOperation {
   organisationId: number;
   entityType: BulkClosingEntityType;
   entityId: number;
}

export interface BulkClosingPayload {
   isEnabled: boolean;
   operations: BulkClosingOperation[];
   reportingStandardId?: number;
}

export interface BulkClosingResult {
   success: boolean;
   isEnabled: boolean;
   requested: number;
   updated: number;
   created: number;
   skippedOperationCount: number;
   skippedRowCount: number;
   closedRows?: number;
   openedRows?: number;
   skipped: Array<BulkClosingOperation & { reason: string }>;
}

export type BulkClosingResponse = BulkClosingResult | { jobId: number; status: 'PENDING' };

export interface ClosingMatrixOutOfScopeCell {
   address: string;
   organisationId: number;
   key: string;
}

export interface ClosingMatrixImportSummary {
   cells: number;
   closes: number;
   opens: number;
   unchanged: number;
   entities: number;
   outOfScope: number;
   outOfScopeCells: ClosingMatrixOutOfScopeCell[];
   reportingStandardId: number | null;
}

export interface ClosingMatrixImportError {
   address: string;
   code: 'INVALID_VALUE' | 'INVALID_KEY' | 'INVALID_ORGANISATION' | 'MISSING_META' | 'MISSING_SHEET';
   detail?: string;
}

export type ClosingMatrixImportResponse =
   | ({ dryRun: true } & ClosingMatrixImportSummary)
   | ({ success: true; noChanges: true } & ClosingMatrixImportSummary)
   | (BulkClosingResponse & { summary: ClosingMatrixImportSummary });

export interface PeriodsState extends EntityState<Period>, ActiveState {
   hierarchicalOrganisations: any; // Can also be null, which is covered by any type
   isHierarchicalOrganisationsLoading: boolean;
   entityStatusRecords: EntityStatusRecord[] | null;
   validationStatusRecords: ValidationStatusRecord[] | null;
   completionStats: CompletionStats | null;
   isCompletionStatsLoading: boolean;
   kpiDetailsData: KpiDetailsData;
   isLoadingKpiDetails: boolean;
   kpiDefinitions: KpiDefinitions | null; // Normalized KPI definitions keyed by reportingStandardId
}

const store = createEntityStore<PeriodsState>(
   {
      hierarchicalOrganisations: null,
      isHierarchicalOrganisationsLoading: false,
      entityStatusRecords: null,
      validationStatusRecords: null,
      completionStats: null,
      isCompletionStatsLoading: false,
      kpiDetailsData: null,
      isLoadingKpiDetails: false,
      kpiDefinitions: null,
   },
   { resettable: true, name: 'periods' }
);

export default store;
